import { useState, useEffect, useCallback, useRef } from 'react'
import { glorifyApiClient, GlorifyApiError } from '../../../services/api/glorify'
import type { AudioRecord } from '../../../services/api/glorify'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function statusChipClass(statusId: number): string {
  switch (statusId) {
    case 1: return 'gd-status-chip gd-status-chip--processing'
    case 2: return 'gd-status-chip gd-status-chip--completed'
    case 3: return 'gd-status-chip gd-status-chip--failed'
    default: return 'gd-status-chip gd-status-chip--pending'
  }
}

function statusLabel(statusId: number): string {
  switch (statusId) {
    case 0: return 'Pending'
    case 1: return 'Processing'
    case 2: return 'Completed'
    case 3: return 'Failed'
    default: return 'Unknown'
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

type UploadState = 'idle' | 'uploading' | 'success' | 'error'

export default function AudioPage(): React.JSX.Element {
  const [audios, setAudios] = useState<AudioRecord[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [listError, setListError] = useState<string | null>(null)
  const [uploadState, setUploadState] = useState<UploadState>('idle')
  const [uploadMessage, setUploadMessage] = useState<string>('')
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const fetchAudios = useCallback(async (): Promise<void> => {
    setListError(null)
    try {
      const resp = await glorifyApiClient.listAudios()
      setAudios(resp.audios)
    } catch (err) {
      setListError(
        err instanceof GlorifyApiError ? err.message : 'Failed to load audio files'
      )
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchAudios()
  }, [fetchAudios])

  const handleUpload = useCallback(
    async (file: File): Promise<void> => {
      setUploadState('uploading')
      setUploadMessage(`Uploading ${file.name}…`)
      try {
        const resp = await glorifyApiClient.uploadAudio(file)
        setUploadState('success')
        setUploadMessage(`Uploaded "${resp.filename}" (${formatFileSize(resp.size)})`)
        await fetchAudios()
        setTimeout(() => setUploadState('idle'), 3000)
      } catch (err) {
        setUploadState('error')
        setUploadMessage(
          err instanceof GlorifyApiError ? err.message : 'Upload failed'
        )
      }
    },
    [fetchAudios]
  )

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0]
    if (file) void handleUpload(file)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files[0]
    if (file && uploadState !== 'uploading') void handleUpload(file)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (): void => setIsDragging(false)

  return (
    <>
      <div className="gd-page-header">
        <h1>Audio</h1>
        <p>Upload and manage your audio files.</p>
      </div>

      {/* Upload zone */}
      <div
        className={`gd-upload-zone${isDragging ? ' gd-upload-zone--dragging' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => uploadState !== 'uploading' && fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        aria-label="Upload audio — click or drag and drop"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            if (uploadState !== 'uploading') fileInputRef.current?.click()
          }
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
          aria-hidden="true"
        />
        <div>
          <span className="material-icons" aria-hidden="true">cloud_upload</span>
        </div>
        <p>
          <strong>Click to upload</strong> or drag and drop an audio file here
        </p>
        <p style={{ fontSize: 12 }}>MP3, WAV, FLAC, AAC, OGG…</p>
      </div>

      {/* Upload status */}
      {uploadState !== 'idle' && (
        <div
          className={`gd-upload-status gd-upload-status--${uploadState}`}
          role={uploadState === 'error' ? 'alert' : 'status'}
        >
          <span className="material-icons" aria-hidden="true">
            {uploadState === 'uploading'
              ? 'hourglass_top'
              : uploadState === 'success'
                ? 'check_circle'
                : 'error'}
          </span>
          {uploadMessage}
        </div>
      )}

      {/* List error */}
      {listError && (
        <div className="gd-banner gd-banner--error" role="alert">
          <span className="material-icons" aria-hidden="true">warning</span>
          <p>{listError}</p>
        </div>
      )}

      {/* Table or empty state */}
      {isLoading ? (
        <div className="gd-empty">
          <div className="gd-spinner" aria-label="Loading audio files" />
        </div>
      ) : audios.length === 0 ? (
        <div className="gd-empty">
          <span className="material-icons" aria-hidden="true">volume_off</span>
          <p>No audio files yet. Upload your first file above.</p>
        </div>
      ) : (
        <div className="gd-table-wrap">
          <table className="gd-table">
            <thead>
              <tr>
                <th>File name</th>
                <th>Type</th>
                <th>Size</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {audios.map((aud) => (
                <tr key={aud.audio_id}>
                  <td>{aud.audio_file_name}</td>
                  <td className="gd-cell-muted">{aud.audio_file_type}</td>
                  <td className="gd-cell-muted">{formatFileSize(aud.audio_file_size)}</td>
                  <td>
                    <span className={statusChipClass(aud.audio_status_id)}>
                      {statusLabel(aud.audio_status_id)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
